import { useState, useEffect, useRef } from 'react';
import { Tabs } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import { SearchInput } from '@halo/ui-chat-react';
import { useLocation } from 'react-router';
import Paths from '@/configs/router/paths';
import { useActionsStore } from '@/store/actions';
import { DialogTrigger } from './DialogTrigger';
import type { SearchResults } from './helpers';
import { SearchTabs } from './helpers';
import { defaultSearchResults, getTabs, useSearch } from './helpers';

interface GlobalSearchDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  children: React.ReactNode;
}

function GlobalSearchDialog({ isOpen, setIsOpen, children }: GlobalSearchDialogProps) {
  const { t } = useTranslation();
  const getGlobalSearchTab = useActionsStore((state) => state.getGlobalSearchTab);

  const [active, setActive] = useState<SearchTabs>(SearchTabs.TopResults);
  const [value, setValue] = useState('');
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 50, width: 0 });
  const [searchResults, setSearchResults] =
    useState<Record<SearchTabs, SearchResults>>(defaultSearchResults);
  const { onSearch } = useSearch(value, active, setSearchResults, isOpen);
  const tabs: Record<any, any>[] = getTabs(value, searchResults, setActive, setValue, active);

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (active) {
      onSearch();
    }
  }, [active, onSearch]);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      const defaultSearchTab = getGlobalSearchTab();
      if (defaultSearchTab) {
        setActive(defaultSearchTab);
      }
      setSearchResults(defaultSearchResults);
    }
  }, [isOpen, getGlobalSearchTab, setValue]);

  const location = useLocation();

  useEffect(() => {
    if (location?.pathname === Paths.CONTACTS && isOpen) {
      setActive(SearchTabs.Contacts);
    }
  }, [location?.pathname, isOpen, setActive]);

  useEffect(() => {
    if (!isOpen) return;

    const updateIndicator = () => {
      const activeTab = tabRefs.current[active];
      const list = listRef.current;

      if (activeTab && list) {
        const listRect = list.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const left = tabRect.left - listRect.left;
        const width = tabRect.width;

        setIndicatorStyle({ left: left + width / 2, width });
      }
    };

    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [active, isOpen]);

  return (
    <Dialog.Root
      lazyMount
      placement="top"
      size="lg"
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
    >
      {children}
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content w="560px" maxW="100%" overflow="hidden">
          <Dialog.Header px="5" pt="4" pb="2" alignItems="center">
            <SearchInput
              value={value}
              onChange={setValue}
              inputProps={{
                placeholder:
                  tabs.find((tab) => tab.value === active)?.searchPlaceholder ||
                  t('filter.searchPlaceholder'),
              }}
            />
            {/* <Dialog.CloseTrigger asChild position="relative" right="0" top="0">
                <CloseButton size="sm" />
              </Dialog.CloseTrigger> */}
          </Dialog.Header>
          <Dialog.Body p="0">
            <Tabs.Root
              value={active}
              size="sm"
              onValueChange={(details) => setActive(details.value as SearchTabs)}
            >
              <Tabs.List
                ref={listRef}
                border="none"
                gap="6"
                px="5"
                className="List"
                pos="relative"
                _before={{
                  content: '""',
                  pos: 'absolute',
                  bottom: 0,
                  left: `${indicatorStyle.left}px`,
                  transform: `translateX(-50%)`,
                  w: `3`,
                  h: '0.5',
                  background: 'fg',
                  transition:
                    'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {tabs?.map((item) => (
                  <Tabs.Trigger
                    ref={(el) => {
                      tabRefs.current[item.value] = el;
                    }}
                    value={item.value}
                    key={item.value}
                    _before={{ content: 'none' }}
                    px="0"
                  >
                    {item.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              {tabs.map((item) => (
                <Tabs.Content
                  className="scrollHover"
                  value={item.value}
                  key={item.value}
                  px="5"
                  pb="5"
                  bg="bg.subtle"
                  h="400px"
                  overflowY="auto"
                >
                  {item.content}
                </Tabs.Content>
              ))}
            </Tabs.Root>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

export { GlobalSearchDialog, DialogTrigger };
